// libraries
import type { DealState } from '@makemydeal/dr-dash-types';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';

// components
import { Grid } from '@interstate/components/Grid';

import LineItemWithChildren from '../common/LineItemWithChildren';

// constants
import * as constants from '../../constants';

// utils
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import type { IDealSummaryItemProps } from '../common/DealSummaryItem';
import DealSummaryItem from '../common/DealSummaryItem';

// hooks
import { createDealRootSelector } from '../../offerCompareColumn/hooks/createDealRootSelector';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import {
    detailedViewLabelStyles,
    detailedViewValueStyles,
    labelStyles as defaultLabelStyles,
    secondaryValueStyles,
    valueStyles as defaultValueStyles,
    detailedViewGridProps,
    detailedViewSecondaryGridProps,
    expandibleAreaStyles,
    expandibleButtonStyles,
    expandibleButtonStylesTradeIn
} from '../utils/styles';

import { useDealSummary } from '../../common/DealSummaryProvider';
import { useMediaQuery } from 'react-responsive';
import { BreakPoint } from '@makemydeal/dr-activities-common';
import { useTrackedSelector } from '../../telemetry/useTrackedSelector';
import { useHeldValue } from '../../hooks/useHeldSelector';

export type NetTradeProps = DealState & {
    useWrapper?: boolean;
    hasDelta?: boolean;
};

const NetTrade = ({ deal, useWrapper = true, hasDelta = false }: NetTradeProps) => {
    const useDealRootSelector = createDealRootSelector(deal, 'NetTrade');
    const isLargeSize = useMediaQuery({ query: `(min-width: ${BreakPoint.MEDIUM})` });
    const { showDetailedView } = useDealSummary();

    const isEnhancedDealDetailsEnabled = useTrackedSelector(featureToggleSelectors.getEnhancedDealDetails, {
        component: 'NetTrade',
        field: 'enhancedDealDetails'
    });
    const isDealScopedNetTradeEnabled = useTrackedSelector(featureToggleSelectors.isDealScopedNetTradeEnabled, {
        component: 'NetTrade',
        field: 'dealScopedNetTrade'
    });
    const useTradeInsRootSelector = createDealRootSelector(isDealScopedNetTradeEnabled ? deal : undefined, 'NetTrade');

    const netCashPayoutValue = useHeldValue(useDealRootSelector(offerReduxSelectors.getNetCashPayout, 'netCashPayout'), deal);
    const tradeIns = useTradeInsRootSelector(
        tradeInsSelectors.getIncludedTradeInsWithOriginalIndexes,
        'includedTradeInsWithOriginalIndexes'
    );
    const netTradeValueForActiveTradeIns = useTradeInsRootSelector(
        tradeInsSelectors.getTotalNetTradeValueForIncludedActiveTradeIns,
        'totalNetTradeValueForIncludedActiveTradeIns'
    );
    let gridProps = {};
    if (showDetailedView && isLargeSize) {
        gridProps = {
            ...detailedViewGridProps,
            pt: '0.571rem'
        };
    } else {
        gridProps = detailedViewSecondaryGridProps;
    }

    const secondaryDealSummaryItemProps = isEnhancedDealDetailsEnabled
        ? {
              expandibleButtonStyles: {
                  ...expandibleButtonStyles,
                  ...expandibleButtonStylesTradeIn
              },
              expandibleAreaStyles: expandibleAreaStyles
          }
        : ({
              useWrapper: useWrapper,
              hasDelta: hasDelta,
              expandibleButtonStyles: expandibleButtonStylesTradeIn
          } as Partial<IDealSummaryItemProps>);
    return (
        <DealSummaryItem
            label={constants.MULTI_NET_TRADE}
            value={formatDollarsAndCents(netTradeValueForActiveTradeIns)}
            valueStyles={showDetailedView ? detailedViewValueStyles : defaultValueStyles}
            labelStyles={showDetailedView ? {} : defaultLabelStyles}
            hasDelta={hasDelta}
        >
            <Grid sx={isEnhancedDealDetailsEnabled ? gridProps : {}}>
                {(tradeIns || []).map(({ tradeIn: { amountOwed, value, year, make, model }, originalIndex }) => {
                    const vehicleDetails = [year, make, model].filter(Boolean).join(' ');
                    return (
                        <DealSummaryItem
                            key={`trade-${originalIndex}`}
                            {...secondaryDealSummaryItemProps}
                            valueStyles={secondaryValueStyles}
                            labelStyles={{
                                ...(showDetailedView ? detailedViewLabelStyles : defaultLabelStyles),
                                mr: 1,
                                flex: 1
                            }}
                            label={`Trade ${originalIndex + 1}${vehicleDetails ? `: ${vehicleDetails}` : ''}`}
                            value={formatDollarsAndCents(value - amountOwed)}
                        >
                            <LineItemWithChildren
                                label={constants.ALLOWANCE}
                                value={formatDollarsAndCents(value)}
                                containerStyles={{
                                    pr: 0
                                }}
                                {...(isEnhancedDealDetailsEnabled && {
                                    containerStyles: {
                                        pr: 0
                                    }
                                })}
                            />
                            <LineItemWithChildren
                                label={constants.PAYOFF}
                                value={formatDollarsAndCents(amountOwed)}
                                containerStyles={{
                                    pr: 0
                                }}
                                {...(isEnhancedDealDetailsEnabled && {
                                    containerStyles: {
                                        pr: 0
                                    }
                                })}
                            />
                        </DealSummaryItem>
                    );
                })}
                {netCashPayoutValue > 0 && (
                    <DealSummaryItem
                        label={constants.NET_CASH_PAYOUT}
                        value={formatDollarsAndCents(netCashPayoutValue)}
                        labelStyles={{
                            ...(showDetailedView ? detailedViewLabelStyles : defaultLabelStyles),
                            mr: 1,
                            flex: 1
                        }}
                        valueStyles={secondaryValueStyles}
                    />
                )}
            </Grid>
        </DealSummaryItem>
    );
};

export default NetTrade;
